import React, { useEffect, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerIngresoPorId, editarIngresoLibre } from '../services/ingresosService';
import { obtenerClientes } from '../services/clientesService';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import NuevoClienteInline from './NuevoClienteInline';
import { CANALES } from '../utils/canales';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Corrige un ingreso libre / venta libre ya registrado desde el Balance.
// Al guardar, el dinero se ajusta solo en las cuentas (reverso atómico).
export default function EditarIngresoModal({ referencia, onCerrar, onGuardado }) {
  const { mostrarToast } = useToast();
  const [form, setForm] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    Promise.all([obtenerIngresoPorId(referencia), obtenerCuentas(), obtenerClientes().catch(() => [])])
      .then(([i, cs, cl]) => {
        setCuentas(cs);
        setClientes(cl);
        setForm({
          concepto: i.concepto || '',
          // monto = precio - descuento + envío  ⇒  precio = monto + descuento - envío.
          precio: String(((Number(i.monto) || 0) + (Number(i.descuento) || 0) - (Number(i.envio) || 0)) || ''),
          descuento: String(Number(i.descuento) || 0),
          envio: String(Number(i.envio) || 0),
          costo: String(Number(i.costo) || 0),
          cuentaId: i.cuenta_id || (cs[0]?.id || ''),
          clienteId: i.cliente_id || '',
          fecha: i.fecha || '',
          canal: i.canal || '',
        });
      })
      .catch((e) => setError(mensajeAmigable(e)));
  }, [referencia]);

  const precioNum = numeroSeguro(form?.precio);
  const descuentoNum = numeroSeguro(form?.descuento);
  const envioNum = numeroSeguro(form?.envio);
  const costoNum = numeroSeguro(form?.costo);
  const netoProducto = Math.max(0, precioNum - descuentoNum);
  const neto = netoProducto + envioNum;
  const ganancia = netoProducto - costoNum;

  const guardar = async () => {
    if (!form.concepto.trim()) { mostrarToast('Escribe el concepto', 'error'); return; }
    if (descuentoNum > precioNum) { mostrarToast('El descuento no puede ser mayor al precio', 'error'); return; }
    if (neto <= 0) { mostrarToast('El total que entra debe ser mayor a 0 (precio o envío)', 'error'); return; }
    setGuardando(true);
    try {
      await editarIngresoLibre(referencia, { ...form, monto: neto, descuento: descuentoNum, envio: envioNum, costo: costoNum, clienteId: form.clienteId || null });
      mostrarToast('Ingreso corregido. Dinero ajustado ✅', 'exito');
      onGuardado?.();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const label = { fontSize: '12px', color: '#6B7280', fontWeight: 600, display: 'block', marginBottom: '4px' };

  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1200 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#ECFDF5', borderRadius: '16px 16px 0 0' }}>
          <h3 style={{ margin: 0, color: 'var(--k-verde)', fontSize: '18px' }}>✏️ Editar ingreso</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
            Al guardar, el dinero se ajusta solo en tus cuentas.
          </p>
        </div>

        <div style={{ padding: '20px' }}>
          {error && <p style={{ color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
          {!form && !error && <p style={{ color: '#6B7280', textAlign: 'center', padding: '20px' }}>⏳ Cargando...</p>}
          {form && (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={label}>¿Qué vendiste o de qué es el ingreso? *</label>
                <input type="text" value={form.concepto} disabled={guardando}
                  onChange={(e) => setForm({ ...form, concepto: e.target.value })} style={input}
                  placeholder="Ej: Perfume sin registrar / Consultoría" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div>
                  <label style={label}>Precio de venta (L)</label>
                  <CampoNumero valor={form.precio} onChange={(v) => setForm({ ...form, precio: v })} deshabilitado={guardando}
                    min={0} prefijo="L" placeholder="0.00" estilo={input} />
                </div>
                <div>
                  <label style={label}>Costo (L) — opcional</label>
                  <CampoNumero valor={form.costo} onChange={(v) => setForm({ ...form, costo: v })} deshabilitado={guardando}
                    min={0} prefijo="L" placeholder="0.00" estilo={input} />
                </div>
                <div>
                  <label style={label}>Descuento (L)</label>
                  <CampoNumero valor={form.descuento} onChange={(v) => setForm({ ...form, descuento: v })} deshabilitado={guardando}
                    min={0} max={precioNum || undefined} prefijo="L" placeholder="0.00"
                    maxMensaje="El descuento no puede ser mayor al precio" estilo={input} />
                </div>
                <div>
                  <label style={label}>Envío (L)</label>
                  <CampoNumero valor={form.envio} onChange={(v) => setForm({ ...form, envio: v })} deshabilitado={guardando}
                    min={0} prefijo="L" placeholder="0.00" estilo={input} />
                </div>
              </div>

              {costoNum > 0 && netoProducto > 0 && (
                <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: ganancia >= 0 ? '#F0FDF4' : '#FEF2F2', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${ganancia >= 0 ? '#BBF7D0' : '#FECACA'}` }}>
                  <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>📈 Ganancia (sin envío)</span>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: ganancia >= 0 ? 'var(--k-verde)' : '#DC2626' }}>
                    {fmt(ganancia)}{precioNum > 0 && costoNum > 0 ? `  ·  ${Math.round((ganancia / costoNum) * 100)}%` : ''}
                  </span>
                </div>
              )}
              <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: '#ECFDF5', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#065F46', fontWeight: 600 }}>Total que entra</span>
                  <span style={{ fontSize: '18px', color: 'var(--k-verde)', fontWeight: 'bold' }}>{fmt(neto)}</span>
                </div>
                {envioNum > 0 && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--k-verde)' }}>
                    Incluye {fmt(netoProducto)} del producto + {fmt(envioNum)} de envío 📦
                  </p>
                )}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={label}>¿A qué cuenta entra el dinero?</label>
                <select value={form.cuentaId} disabled={guardando} onChange={(e) => setForm({ ...form, cuentaId: e.target.value })} style={input}>
                  {cuentas.length === 0 && <option value="">Sin cuentas</option>}
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={label}>Canal de venta</label>
                <select value={form.canal} disabled={guardando} onChange={(e) => setForm({ ...form, canal: e.target.value })} style={input}>
                  <option value="">Sin especificar</option>
                  {CANALES.map((c) => <option key={c.valor} value={c.valor}>{c.icono} {c.valor}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '18px' }}>
                <div>
                  <label style={label}>Cliente (opcional)</label>
                  <select value={form.clienteId} disabled={guardando} onChange={(e) => setForm({ ...form, clienteId: e.target.value })} style={input}>
                    <option value="">Sin cliente</option>
                    {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Fecha</label>
                  <input type="date" value={form.fecha} disabled={guardando} onChange={(e) => setForm({ ...form, fecha: e.target.value })} style={input} />
                </div>
              </div>

              <div style={{ marginTop: '-8px', marginBottom: '18px' }}>
                <NuevoClienteInline disabled={guardando} onCreado={(c) => {
                  setClientes((prev) => [...prev, c].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
                  setForm((f) => ({ ...f, clienteId: c.id }));
                }} />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={onCerrar} disabled={guardando}
                  style={{ flex: '0 0 auto', padding: '12px 18px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '10px', cursor: 'pointer', color: '#6B7280', fontWeight: 600 }}>
                  Cancelar
                </button>
                <button onClick={guardar} disabled={guardando}
                  style={{ flex: 1, padding: '12px', backgroundColor: guardando ? '#9CA3AF' : 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
                  {guardando ? '⏳ Guardando...' : `Guardar ${neto > 0 ? fmt(neto) : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
